import { createMqttClient } from "./create-mqtt-client";
import type { BrokerConfig, MqttJsClient } from "./types";

export * from "./types";

let sharedInstance: MqttJsClient | undefined;

/**
 * Creates or returns the shared singleton MQTT client.
 * Use this in application code so all components share a single connection.
 */
export function getSharedMqttJsClient(config: BrokerConfig): MqttJsClient {
	if (!sharedInstance) {
		sharedInstance = createMqttClient(config);
	} else if (import.meta.env.DEV) {
		console.warn(
			"[mqtt-client] getSharedMqttJsClient called again — existing instance returned. " +
				"Call getMqttJsClient().reconnect(config) if you need to move it to a different broker " +
				"(preserves existing subscriptions), or resetSharedMqttJsClient() to tear it down entirely.",
		);
	}
	return sharedInstance;
}

/**
 * Returns the existing shared client. Throws if it hasn't been created yet.
 */
export function getMqttJsClient(): MqttJsClient {
	if (!sharedInstance) {
		throw new Error(
			"[mqtt-client] getMqttJsClient called before shared instance was created. " +
				"Call getSharedMqttJsClient(config) first.",
		);
	}
	return sharedInstance;
}

/**
 * Tears down the shared singleton client.
 */
export function resetSharedMqttJsClient(): void {
	if (sharedInstance) {
		sharedInstance.disconnect();
		sharedInstance = undefined;
	}
}

/**
 * Creates a new independent MQTT client instance (non-singleton).
 */
export function useMqttJs(config: BrokerConfig): MqttJsClient {
	return createMqttClient(config);
}
